/* eslint-disable */
// useAnnouncementAudience — who this user may send to, and how many people that
// is BEFORE the server filters.
//
// ⚠️ THE COUNT IS AN ESTIMATE AND MUST BE LABELLED AS ONE. What we can compute
// here is CANDIDATES: users in scope before the database applies portal access,
// type muting, and the never-notify-yourself rule. The true figure only exists
// after the send, in the envelope's `recipients`. Hence "up to N" everywhere in
// the UI, and the real numbers only in the confirmation.
//
// ⚠️ THE UI RESTRICTION IS NOT THE SECURITY BOUNDARY. The database re-checks
// every audience; a tampered payload earns `audience_not_permitted`, not a wider
// send. What this hook does is hide what the user may not do — so they are not
// offered a button that then refuses them. Both halves are required.
//
// Reads come from useAccessControlData (Gate D — the hook that already exists;
// imported, never copied) plus usePortalPermissions for "who am I".

import { useMemo } from 'react';
import { useAccessControlData } from '../portals/admin/accessControl/hooks/useAccessControlData';
import { usePortalPermissions } from './usePortalPermissions';

// Roles that count as administering a portal, for the purpose of sending to it.
const ADMIN_ROLES = ['admin'];

export function useAnnouncementAudience({ hasItem, itemPortal = 'lens' }) {
  const { users = [], permissions = [], loading } = useAccessControlData();
  const { data: mine } = usePortalPermissions();

  const isSuperAdmin = !!mine?.isSuperAdmin;

  // ⚠️ THE SENDER IS NEVER A RECIPIENT. Step 0 of the resolution order refuses to
  // notify the actor of their own action, so an estimate that counts the signed-in
  // user promises one more person than can ever receive it: "up to 48" comes back
  // as recipients: 47, which reads exactly like an off-by-one bug and teaches the
  // admin to distrust the number. Subtracted from every mode below.
  const myId = mine?.user?.id || null;

  // The portals this user administers. Convention #89: portal role comes from
  // user_portal_permissions, never from user_profiles.role.
  const adminPortals = useMemo(() => {
    if (!mine?.permissions) return [];
    return mine.permissions
      .filter((p) => ADMIN_ROLES.includes(p.role))
      .map((p) => p.portal_id);
  }, [mine]);

  // May this user send at all, and in which shapes?
  //   system-wide (no item)  → super admin only; the DB refuses a portal admin
  //                            on a NULL-portal type, so offering it would be a
  //                            button that always fails.
  //   with an item           → super admin, or an admin of the item's portal.
  const canSendSystemWide = isSuperAdmin;
  const administersItemPortal = isSuperAdmin || adminPortals.includes(itemPortal);
  const canSend = hasItem ? administersItemPortal : canSendSystemWide;

  // Which of the three modes to offer. A portal admin never gets "Everyone" —
  // that is a system-wide send by definition.
  const modes = useMemo(() => {
    if (isSuperAdmin) return ['all', 'portal', 'users'];
    if (hasItem && administersItemPortal) return ['portal', 'users'];
    return [];
  }, [isSuperAdmin, hasItem, administersItemPortal]);

  // Portals offered: everything present in the permission matrix for a super
  // admin, only the administered ones otherwise.
  const portalOptions = useMemo(() => {
    const counts = {};
    permissions.forEach((p) => {
      if (!p.portal_id || p.role === 'none') return;
      counts[p.portal_id] = (counts[p.portal_id] || 0) + 1;
    });
    const ids = isSuperAdmin ? Object.keys(counts) : adminPortals.filter((p) => p in counts);
    return ids.sort().map((id) => ({ id, count: counts[id] }));
  }, [permissions, isSuperAdmin, adminPortals]);

  // People offered. A super admin sees everyone; a portal admin sees only users
  // who can actually reach the portal they are announcing to — offering the rest
  // would be offering a send that the server will filter to nothing.
  const userOptions = useMemo(() => {
    const reachable = new Set(
      permissions
        .filter((p) => p.role && p.role !== 'none' && (isSuperAdmin || p.portal_id === itemPortal))
        .map((p) => p.user_id),
    );
    return users
      .filter((u) => isSuperAdmin || reachable.has(u.id))
      .map((u) => ({
        id: u.id,
        name: u.display_name || (u.email ? u.email.split('@')[0] : 'Unknown'),
        email: u.email,
        role: u.role || '—',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, permissions, isSuperAdmin, itemPortal]);

  // Candidate count for a given selection. "Up to" — see the header.
  const candidateIds = (kind, spec) => {
    if (kind === 'all') return new Set(users.map((u) => u.id));
    if (kind === 'portal') {
      const picked = new Set(spec?.portals || []);
      if (!picked.size) return new Set();
      return new Set(
        permissions
          .filter((p) => picked.has(p.portal_id) && p.role && p.role !== 'none')
          .map((p) => p.user_id),
      );
    }
    if (kind === 'users') return new Set(spec?.user_ids || []);
    return new Set();
  };

  // The sender is removed here, once, so every mode inherits it.
  const estimateFor = (kind, spec) => {
    const ids = candidateIds(kind, spec);
    if (myId) ids.delete(myId);
    return ids.size;
  };

  // True when something IS selected but the only person in it is the sender —
  // the send would be accepted and reach nobody. Worth blocking up front rather
  // than letting it become a zero-recipient round trip.
  const selfOnly = (kind, spec) => {
    const ids = candidateIds(kind, spec);
    if (!ids.size || !myId) return false;
    return ids.size === 1 && ids.has(myId);
  };

  return {
    loading,
    isSuperAdmin,
    canSend,
    modes,
    portalOptions,
    userOptions,
    estimateFor,
    selfOnly,
    myId,
    // Also sender-excluded, so the "Everyone" line agrees with the footer.
    totalPeople: users.filter((u) => u.id !== myId).length,
  };
}

// useCanAnnounce — the ENTRY-POINT gate. Deliberately light: it reads only
// usePortalPermissions (already cached app-wide) and never useAccessControlData,
// because a lens card renders many times per screen and must not each pull the
// full user list.
//
// Same rule as useAnnouncementAudience's canSend, same two sources (Conv #89):
// portal role from user_portal_permissions, super admin from user_profiles.
// A user who may not send must never see a button that then refuses them.
export function useCanAnnounce(portal = 'lens') {
  const { data } = usePortalPermissions();
  if (!data) return false;                      // fail-closed while loading
  if (data.isSuperAdmin) return true;
  return (data.permissions || []).some(
    (p) => p.portal_id === portal && ADMIN_ROLES.includes(p.role),
  );
}
